import { ShopError } from '../..';
import { forEach, forOwn } from '../../utils';
import { CartProduct, } from './../../cart/item';
import { CouponType, CouponAction, } from '.';
/**
 *  All coupon logic. (!) Used for both the shop and the cart.
 */
export default {
    /**
     *
     *  Used to get the items to which a specific forwarded coupon applies.
     *  If the coupon does not specify any products (an empty product prop)
     *  it means it should be applied on all the forwarded products.
     *
     *  (i) As the location of the ticket of a product differs per type of
     *  product, a method for retrieving their ticket should be forwarded.
     *
     *  @return a sorted Array of items on which the forwarded coupon
     *  applies, descending from the most expensive
     *
     */
    getApplicableItems(coupon, products, getTicketOfProduct) {
        const appliesTo = [];
        /**
         *  Used to add the product to the Array of items on which the
         *  coupon applies. Also save the pivot if forwarded to enable the
         *  method for applying the discount to take the amount_override
         *  into account.
         */
        const appliesToProduct = (product, pivot) => {
            appliesTo.push({
                product,
                ticket: getTicketOfProduct(product),
                pivot,
                product_original_pricing: product.item.pricing,
            });
        };
        // The coupon applies to all the forwarded products if the
        // returned coupon data does not specify any products
        if (!coupon.coupon.products.length) {
            forOwn(products, (product) => {
                appliesToProduct(product);
            });
        }
        else {
            forEach(coupon.coupon.products, (couponProduct) => {
                forOwn(products, (product) => {
                    if (product.item.guid === couponProduct.guid) {
                        appliesToProduct(product, couponProduct.pivot);
                    }
                });
            });
        }
        // Start applying or removing the discount descending from the most expensive product
        appliesTo.sort((caA, caB) => {
            // (!) Use the original price to make sure the sorting keeps
            // working as expected after a coupon has been applied
            // The pricing of the product that should be used is located in a
            // product's item prop when it's about a cart item
            const pricingA = caA.product_original_pricing;
            const pricingB = caB.product_original_pricing;
            return pricingA.base_price === pricingB.base_price
                ? 0
                : (pricingA.base_price > pricingB.base_price ? -1 : 1);
        });
        return appliesTo;
    },
    /**
     *  Used to get the amount of discount of a coupon, taking the amount_override
     *  for a specific product (based on the pivot) and the already spend amount
     *  into account in case of a cheque.
     */
    getAmount(coupon, couponApplicable) {
        let amount = coupon.item.coupon.amount;
        if (coupon.item.coupon.type === CouponType.Cheque) {
            amount = Math.max(0, (amount - coupon.spend));
        }
        else if (couponApplicable.pivot && typeof couponApplicable.pivot.amount_override === 'number') {
            amount = couponApplicable.pivot.amount_override;
        }
        return amount;
    },
    /**
     *
     *  Method for applying a coupon discount; either adding or removing it.
     *
     *  (!) Applied on the pricing object to the cart item (product) itself, which is a copy of the
     *  original item's item pricing object, so the original product pricing remains unchanged.
     *
     *  @return When it's about adding the amount of used is returned because it might have used just
     *  a part of the total computed delta (if the delta is bigger than the current price), and the
     *  negative delta should be returned if it's about removing because the delta got removed. Doing
     *  it like this allows users of the method to always plus the returned delta.
     *
     *  @todo How to handle an applied new-price coupon? Is it always leading?
     *
     */
    applyDiscount(coupon, action, couponApplicable) {
        // Discount is always applied to the pricing object of the product, as it's
        // both in a ticket product and cart product the object that's used for
        // reflecting the current pricing, including any discounts
        const productPricing = couponApplicable.product.pricing;
        // The coupon's applied delta is saved if it was applied to the product
        let delta = productPricing.coupons[coupon.item.guid];
        // Do not add an already applied coupon or remove a coupon that is not applied
        const applied = !!delta;
        if (action === CouponAction.Add && applied ||
            action === CouponAction.Update && applied ||
            action === CouponAction.Remove && !applied) {
            return 0;
        }
        // Compute the discount delta for the product if it was not already
        // computed - the computation happens when adding a coupon
        if (delta === undefined) {
            const amount = this.getAmount(coupon, couponApplicable);
            switch (coupon.item.coupon.type) {
                // The remaining amount of a cheque is returned by the _getAmount() method
                case CouponType.Cheque:
                case CouponType.Fixed:
                    delta = amount;
                    break;
                case CouponType.Percentage:
                    // (!) Use original price
                    delta = (amount / 100) * couponApplicable.product_original_pricing.base_price;
                    break;
                case CouponType.NewPrice:
                    // (!) Use original price
                    delta = couponApplicable.product_original_pricing.base_price - amount;
                    break;
                default:
                    throw new ShopError(`Cannot ${action} coupon code -> Unknown coupon type: ${coupon.item.coupon.type}`);
            }
            // Always apply discounts in full cents
            delta = Math.ceil(delta);
            // Do not apply the discount if the delta is zero, to keep the appliedCount
            // correct and let the discount apply on any following products
            if (delta === 0) {
                return 0;
            }
        }
        let newBase = 0;
        let used = 0;
        switch (action) {
            case CouponAction.Add:
            case CouponAction.Update:
                newBase = Math.max(0, (productPricing.base_price - delta));
                used = productPricing.base_price < delta
                    ? productPricing.base_price
                    : productPricing.base_price - newBase;
                // The count and spend does not apply for regular products, as this
                // gets used to take the applies_to_count into account it only
                // applies for the cart products
                if (CartProduct.isCartProduct(couponApplicable.product)) {
                    coupon.appliedCount++;
                    coupon.spend += used;
                }
                productPricing.coupons[coupon.item.guid] = used;
                break;
            case CouponAction.Remove:
                newBase = productPricing.base_price + delta;
                if (CartProduct.isCartProduct(couponApplicable.product)) {
                    coupon.appliedCount--;
                    coupon.spend -= delta;
                }
                delete productPricing.coupons[coupon.item.guid];
                break;
        }
        productPricing.base_price = newBase;
        return action === CouponAction.Remove ? -delta : used;
    },
};
//# sourceMappingURL=coupon.js.map